/**
 * Catálogo das legendas de cores dos 22 produtos do GOES-19 (setor NSA).
 *
 * Fontes (detalhes e datas em docs/legendas.md):
 * - Escalas e chaves OFICIAIS do NOAA/STAR: as mesmas imagens que a página de
 *   cada produto mostra (www.star.nesdis.noaa.gov/goes/images/colorbars/),
 *   guardadas sem alteração em public/legends/. Os textos das chaves foram
 *   traduzidos da própria página do STAR e conferidos nos Quick Guides.
 * - Sem escala oficial: GeoColor (cores do guia rápido do CIRA) e bandas 1–6
 *   (tons de cinza da luz do sol refletida; a NOAA não publica escala numérica).
 *
 * As cores das amostras das chaves numeradas foram lidas pixel a pixel das
 * imagens oficiais (e, no GeoColor, dos quadros numerados do guia do CIRA).
 */

export type Daypart = 'day' | 'night';

/** Imagem oficial de escala/chave (arquivo em public/legends/). */
export interface LegendImage {
  file: string;
  /** Tamanho do arquivo (px). */
  width: number;
  height: number;
  /** Só esta faixa vertical é mostrada (ex.: a linha "dia" ou "noite" da chave). A imagem não é alterada. */
  crop?: { y: number; height: number };
  /** Descrição para leitores de tela. */
  alt: string;
}

/** Rótulo explicativo sob a escala, alinhado ao início, meio ou fim dela. */
export interface LegendNote {
  text: string;
  align: 'start' | 'center' | 'end';
}

/**
 * Amostra de cor: uma cor; duas metades lado a lado (`split`), em cima e
 * embaixo (`stacked`) ou na diagonal; ou pontilhado (`speckle`) — como na chave oficial.
 */
export interface Swatch {
  colors: string[];
  pattern?: 'split' | 'stacked' | 'diagonal' | 'speckle';
}

export interface KeyItem {
  /** Número na chave oficial (ausente nas legendas sem imagem oficial). */
  n?: number;
  swatch: Swatch;
  text: string;
}

export interface KeyGroup {
  /** Grupo válido só de dia ou só à noite (a interface escolhe pelo horário do quadro). */
  daypart?: Daypart;
  label?: string;
  image?: LegendImage;
  items: KeyItem[];
}

export interface LegendSource {
  label: string;
  url: string;
}

interface LegendBase {
  productId: string;
  /** O que a escala representa (curto). */
  title: string;
  /** Uma frase de interpretação. */
  hint?: string;
  sources: LegendSource[];
}

/** Escala contínua oficial (imagem do STAR com valores em °C). */
export interface ColorbarLegend extends LegendBase {
  kind: 'colorbar';
  image: LegendImage;
  notes: LegendNote[];
}

/** Degradê feito aqui (bandas de luz refletida, sem escala oficial). */
export interface GradientLegend extends LegendBase {
  kind: 'gradient';
  stops: string[];
  notes: LegendNote[];
}

/** Chave de cores (composições RGB): oficial (com imagem) ou qualitativa. */
export interface KeyLegend extends LegendBase {
  kind: 'key';
  groups: KeyGroup[];
}

export type ProductLegend = ColorbarLegend | GradientLegend | KeyLegend;

// ---------------------------------------------------------------------------
// Fontes
// ---------------------------------------------------------------------------

const STAR_DOCS = 'https://www.star.nesdis.noaa.gov/goes/documents/';

const starPage = (band: string): LegendSource => ({
  label: 'NOAA/STAR',
  url: `https://www.star.nesdis.noaa.gov/goes/sector_band.php?sat=G19&sector=nsa&band=${band}&length=12`,
});

const guide = (label: string, file: string): LegendSource => ({ label, url: STAR_DOCS + file });

const bandGuide = (n: number): LegendSource =>
  guide(`Guia da banda ${n}`, `ABIQuickGuide_Band${String(n).padStart(2, '0')}.pdf`);

// ---------------------------------------------------------------------------
// Imagens oficiais (450 px de largura, como na página do STAR)
// ---------------------------------------------------------------------------

const IMG_BAND7: LegendImage = {
  file: 'ColorBar450Band7_horz.png',
  width: 450,
  height: 62,
  alt: 'Escala de cores oficial da NOAA para a banda 7, de −83 °C (esquerda) a 127 °C (direita).',
};

const IMG_BANDS_8_10: LegendImage = {
  file: 'ColorBar450Bands8-10_horz.png',
  width: 450,
  height: 85,
  alt: 'Escala de cores oficial da NOAA para as bandas de vapor d’água 8 a 10, de −93 °C (nuvens) a 7 °C (ar seco).',
};

const IMG_BANDS_11_15: LegendImage = {
  file: 'ColorBar450Bands11-15_horz.png',
  width: 450,
  height: 62,
  alt: 'Escala de cores oficial da NOAA para as bandas 11 a 15, de −110 °C (esquerda) a 57 °C (direita).',
};

const IMG_BAND16: LegendImage = {
  file: 'ColorBar450Band16_horz.png',
  width: 450,
  height: 62,
  alt: 'Escala de cores oficial da NOAA para a banda 16, de −128 °C (esquerda) a 128 °C (direita).',
};

const IMG_SANDWICH: LegendImage = {
  file: 'ColorBar450_Sandwich_horz.png',
  width: 450,
  height: 62,
  alt: 'Escala de cores oficial da NOAA para o produto Sandwich, de −15 °C (esquerda) a −93 °C (direita, nuvens mais frias).',
};

const IMG_AIRMASS: LegendImage = {
  file: 'ColorBar450AirMass_horz.png',
  width: 450,
  height: 62,
  alt: 'Chave de cores oficial da NOAA para Massas de ar, com 8 cores numeradas.',
};

const IMG_FIRE: LegendImage = {
  file: 'ColorBar450FireTemp.png',
  width: 450,
  height: 62,
  alt: 'Chave de cores oficial da NOAA para Temperatura do fogo, com 9 cores numeradas.',
};

const IMG_DUST: LegendImage = {
  file: 'ColorBar450Dust_horz.png',
  width: 450,
  height: 62,
  alt: 'Chave de cores oficial da NOAA para Poeira, com 8 cores numeradas.',
};

const DNCM_FILE = 'ColorBar450DNCMCombo_horz.png';

// ---------------------------------------------------------------------------
// Textos reaproveitados
// ---------------------------------------------------------------------------

const IR_NOTES: LegendNote[] = [
  { text: 'Muito frio: topo de tempestades', align: 'start' },
  { text: 'Nuvens baixas e médias', align: 'center' },
  { text: 'Chão quente, sem nuvens', align: 'end' },
];

const IR_TITLE = 'Temperatura do topo das nuvens ou do chão (°C)';

const single = (c: string): Swatch => ({ colors: [c] });

function irBand(productId: string, n: number, extra: Partial<ColorbarLegend> = {}): ColorbarLegend {
  return {
    productId,
    kind: 'colorbar',
    title: IR_TITLE,
    image: IMG_BANDS_11_15,
    notes: IR_NOTES,
    hint: 'Quanto mais frio o topo, mais alta a nuvem. As cores vivas marcam as nuvens mais altas.',
    sources: [starPage(productId), bandGuide(n)],
    ...extra,
  };
}

function waterVaporBand(productId: string, n: number, layer: string): ColorbarLegend {
  return {
    productId,
    kind: 'colorbar',
    title: `Umidade do ar em ${layer} (temperatura em °C)`,
    image: IMG_BANDS_8_10,
    notes: [
      { text: 'Nuvens altas', align: 'start' },
      { text: 'Ar úmido', align: 'center' },
      { text: 'Ar seco', align: 'end' },
    ],
    hint: 'Mostra o vapor d’água no ar, não o chão. Tons quentes (amarelo, vermelho) indicam ar seco.',
    sources: [starPage(productId), bandGuide(n)],
  };
}

function reflectanceBand(productId: string, n: number, dark: string, bright: string, hint: string): GradientLegend {
  return {
    productId,
    kind: 'gradient',
    title: 'Luz do sol refletida: quanto mais claro, mais luz',
    stops: ['#000000', '#ffffff'],
    notes: [
      { text: dark, align: 'start' },
      { text: bright, align: 'end' },
    ],
    hint: `${hint} A NOAA não publica escala numérica para esta imagem.`,
    sources: [starPage(productId), bandGuide(n)],
  };
}

// ---------------------------------------------------------------------------
// Catálogo
// ---------------------------------------------------------------------------

const LEGEND_LIST: ProductLegend[] = [
  // Composições
  {
    productId: 'GEOCOLOR',
    kind: 'key',
    title: 'De dia, cores naturais; à noite, nuvens realçadas',
    hint: 'As luzes das cidades vêm de um mapa fixo: não mostram a situação de agora.',
    groups: [
      {
        daypart: 'day',
        label: 'De dia',
        items: [
          { swatch: single('#005000'), text: 'Floresta e vegetação' },
          { swatch: single('#675745'), text: 'Solo seco ou exposto' },
          { swatch: single('#25447f'), text: 'Mar e rios' },
          { swatch: single('#ffffff'), text: 'Nuvens altas e espessas' },
          { swatch: single('#c4a57f'), text: 'Fumaça espessa' },
          { swatch: single('#9da2d0'), text: 'Fumaça fina' },
        ],
      },
      {
        daypart: 'night',
        label: 'À noite',
        items: [
          { swatch: single('#85b4ec'), text: 'Nuvens baixas e nevoeiro' },
          { swatch: single('#9895a0'), text: 'Nuvens médias e altas' },
          { swatch: single('#3b1069'), text: 'Chão sem nuvens' },
          { swatch: single('#f1c337'), text: 'Luzes das cidades' },
        ],
      },
    ],
    sources: [starPage('GEOCOLOR'), guide('Guia do CIRA', 'QuickGuide_CIRA_Geocolor_20171019.pdf')],
  },
  {
    productId: 'FireTemperature',
    kind: 'key',
    title: 'Chave oficial: intensidade do fogo e tipo de cena',
    hint: 'Nuvens escondem o fogo. Solo muito seco e quente pode parecer vermelho sem ser fogo.',
    groups: [
      {
        image: IMG_FIRE,
        items: [
          { n: 1, swatch: single('#db0404'), text: 'Fogo pouco intenso' },
          { n: 2, swatch: single('#ff9650'), text: 'Fogo moderado' },
          { n: 3, swatch: single('#fff07d'), text: 'Fogo intenso' },
          { n: 4, swatch: single('#ffffff'), text: 'Fogo muito intenso' },
          { n: 5, swatch: single('#780f24'), text: 'Área queimada' },
          { n: 6, swatch: { colors: ['#660066', '#ff6699'], pattern: 'diagonal' }, text: 'Chão sem nuvens' },
          { n: 7, swatch: single('#000000'), text: 'Água ou noite' },
          { n: 8, swatch: single('#006699'), text: 'Nuvens de água' },
          { n: 9, swatch: single('#008080'), text: 'Nuvens de gelo' },
        ],
      },
    ],
    sources: [starPage('FireTemperature'), guide('Guia NASA SPoRT', 'QuickGuide_Fire_Temperature_RGB.pdf')],
  },
  {
    productId: 'Sandwich',
    kind: 'colorbar',
    title: 'Temperatura do topo das nuvens (°C)',
    image: IMG_SANDWICH,
    notes: [
      { text: 'Nuvens menos frias', align: 'start' },
      { text: 'Mais frio: tempestades fortes', align: 'end' },
    ],
    hint: 'Onde não há cor aparece a textura das nuvens na luz visível (só de dia).',
    sources: [starPage('Sandwich'), guide('Guia EUMETSAT/EUMeTrain', 'SandwichProduct.pdf')],
  },
  {
    productId: 'AirMass',
    kind: 'key',
    title: 'Chave oficial: massas de ar e nuvens',
    hint: 'As cores podem variar com a hora do dia, a estação e a latitude.',
    groups: [
      {
        image: IMG_AIRMASS,
        items: [
          { n: 1, swatch: single('#b52007'), text: 'Ar seco em altitude' },
          { n: 2, swatch: single('#0a0a8c'), text: 'Massa de ar fria' },
          { n: 3, swatch: single('#548235'), text: 'Ar quente e úmido' },
          { n: 4, swatch: { colors: ['#6a6e36', '#6f4c01'], pattern: 'split' }, text: 'Ar quente, menos úmido' },
          { n: 5, swatch: single('#ffffff'), text: 'Nuvens altas espessas' },
          { n: 6, swatch: single('#b5744f'), text: 'Nuvens médias' },
          { n: 7, swatch: single('#335a25'), text: 'Nuvens baixas' },
          { n: 8, swatch: single('#6e008c'), text: 'Cor falsa na borda' },
        ],
      },
    ],
    sources: [starPage('AirMass'), guide('Guia NASA SPoRT', 'QuickGuide_GOESR_AirMassRGB_final.pdf')],
  },
  {
    productId: 'Dust',
    kind: 'key',
    title: 'Chave oficial: poeira e tipos de nuvem',
    hint: 'À noite a poeira baixa fica roxa. Sobre o mar, nuvens baixas podem parecer poeira.',
    groups: [
      {
        image: IMG_DUST,
        items: [
          { n: 1, swatch: single('#dc389e'), text: 'Poeira (de dia)' },
          { n: 2, swatch: single('#c78be0'), text: 'Nuvens baixas de água' },
          { n: 3, swatch: single('#c4deff'), text: 'Deserto (de dia)' },
          { n: 4, swatch: single('#a37304'), text: 'Nuvens médias espessas' },
          { n: 5, swatch: single('#3a6a0a'), text: 'Nuvens médias finas' },
          { n: 6, swatch: single('#770101'), text: 'Nuvens frias e espessas' },
          { n: 7, swatch: single('#01002a'), text: 'Nuvens altas e finas' },
          { n: 8, swatch: single('#0003d6'), text: 'Nuvem fina, chão quente' },
        ],
      },
    ],
    sources: [starPage('Dust'), guide('Guia NASA SPoRT', 'QuickGuide_Dust_RGB.pdf')],
  },
  {
    productId: 'DayNightCloudMicroCombo',
    kind: 'key',
    title: 'Chave oficial: tipos de nuvem (as cores mudam de dia e à noite)',
    hint: 'De dia mostra se o topo da nuvem é de água ou de gelo; à noite separa nevoeiro de nuvens baixas.',
    groups: [
      {
        daypart: 'day',
        label: 'De dia',
        image: {
          file: DNCM_FILE,
          width: 450,
          height: 200,
          crop: { y: 0, height: 100 },
          alt: 'Chave de cores oficial da NOAA para o dia (Day Cloud Phase), com 8 cores numeradas.',
        },
        items: [
          { n: 1, swatch: { colors: ['#64beca', '#857692'], pattern: 'stacked' }, text: 'Nuvens baixas de água' },
          { n: 2, swatch: single('#5a9e28'), text: 'Nuvens virando gelo' },
          { n: 3, swatch: single('#306c16'), text: 'Neve' },
          { n: 4, swatch: single('#e4b712'), text: 'Nuvens altas espessas' },
          { n: 5, swatch: single('#a2447a'), text: 'Nuvens médias finas' },
          { n: 6, swatch: single('#ed4929'), text: 'Nuvens altas finas' },
          { n: 7, swatch: { colors: ['#002060', '#4472c4'], pattern: 'stacked' }, text: 'Chão' },
          { n: 8, swatch: single('#000000'), text: 'Água' },
        ],
      },
      {
        daypart: 'night',
        label: 'À noite',
        image: {
          file: DNCM_FILE,
          width: 450,
          height: 200,
          crop: { y: 100, height: 100 },
          alt: 'Chave de cores oficial da NOAA para a noite (Night Microphysics), com 10 cores numeradas.',
        },
        items: [
          { n: 1, swatch: single('#7dafd7'), text: 'Nevoeiro' },
          { n: 2, swatch: single('#7de6dc'), text: 'Nuvens muito baixas' },
          { n: 3, swatch: single('#91e691'), text: 'Nuvens baixas e frias' },
          { n: 4, swatch: single('#a0b96e'), text: 'Nuvens médias de água' },
          { n: 5, swatch: single('#95874b'), text: 'Nuvens médias espessas' },
          { n: 6, swatch: single('#0a0a8c'), text: 'Altas, finas, de gelo' },
          { n: 7, swatch: single('#6e008c'), text: 'Altas e muito finas' },
          { n: 8, swatch: single('#820101'), text: 'Nuvens altas espessas' },
          { n: 9, swatch: single('#01011e'), text: 'Altas e finas' },
          { n: 10, swatch: { colors: ['#a60000', '#d27500'], pattern: 'speckle' }, text: 'Muito fria (tempestade)' },
        ],
      },
    ],
    sources: [
      starPage('DayNightCloudMicroCombo'),
      guide('Guia do produto', 'ABIQuickGuide_DayNightCloudMicroCombo.pdf'),
    ],
  },

  // Luz refletida (sem escala oficial)
  reflectanceBand(
    '01',
    1,
    'Escuro: pouca luz (floresta, água)',
    'Claro: muita luz (nuvens)',
    'Fumaça e poeira aparecem como um véu esbranquiçado. À noite fica preta.',
  ),
  reflectanceBand(
    '02',
    2,
    'Escuro: pouca luz (floresta, água)',
    'Claro: muita luz (nuvens)',
    'À noite fica preta (não há luz do sol).',
  ),
  reflectanceBand(
    '03',
    3,
    'Escuro: água e áreas queimadas',
    'Claro: nuvens e vegetação',
    'A vegetação reflete muito esta luz invisível. À noite fica preta.',
  ),
  reflectanceBand(
    '04',
    4,
    'Escuro: chão e nuvens baixas',
    'Claro: nuvens altas e finas (cirros)',
    'O vapor d’água do ar esconde o chão e as nuvens baixas. À noite fica preta.',
  ),
  reflectanceBand(
    '05',
    5,
    'Escuro: água e nuvens de gelo',
    'Claro: nuvens de gotas d’água',
    'À noite fica preta, mas fogo muito intenso pode aparecer claro.',
  ),
  reflectanceBand(
    '06',
    6,
    'Escuro: gelo e gotas grandes',
    'Claro: nuvens de gotas pequenas',
    'À noite fica preta, mas fogo muito intenso pode aparecer claro.',
  ),

  // Infravermelho (escalas oficiais)
  {
    productId: '07',
    kind: 'colorbar',
    title: 'Temperatura medida pelo satélite (°C)',
    image: IMG_BAND7,
    notes: [
      { text: 'Nuvens altas e frias', align: 'start' },
      { text: 'Nuvens baixas e chão', align: 'center' },
      { text: 'Muito quente: focos de fogo', align: 'end' },
    ],
    hint: 'Focos de fogo aparecem como pontos escuros. De dia a luz do sol deixa a imagem mais “quente”.',
    sources: [starPage('07'), bandGuide(7)],
  },
  waterVaporBand('08', 8, 'altitude alta'),
  waterVaporBand('09', 9, 'altitude média'),
  waterVaporBand('10', 10, 'altitude baixa'),
  irBand('11', 11),
  irBand('12', 12, {
    title: 'Temperatura medida pelo satélite (°C)',
    hint: 'O ozônio e o vapor d’água do ar deixam tudo um pouco mais frio nesta banda.',
  }),
  irBand('13', 13),
  irBand('14', 14),
  irBand('15', 15),
  {
    productId: '16',
    kind: 'colorbar',
    title: 'Temperatura medida pelo satélite (°C)',
    image: IMG_BAND16,
    notes: [
      { text: 'Nuvens altas e frias', align: 'start' },
      { text: 'Ar e chão mais quentes', align: 'end' },
    ],
    hint: 'O gás carbônico do ar deixa tudo mais frio nesta banda. Ajuda a estimar a altura das nuvens.',
    sources: [starPage('16'), bandGuide(16)],
  },
];

export const LEGENDS: Readonly<Record<string, ProductLegend>> = Object.fromEntries(
  LEGEND_LIST.map((l) => [l.productId, l]),
);

export function getLegend(productId: string): ProductLegend | undefined {
  return LEGENDS[productId];
}

/** Imagens oficiais usadas por uma legenda. */
export function legendImages(legend: ProductLegend): LegendImage[] {
  if (legend.kind === 'colorbar') return [legend.image];
  if (legend.kind === 'key') return legend.groups.flatMap((g) => (g.image ? [g.image] : []));
  return [];
}

/** A legenda usa a imagem oficial da NOAA (e não só cores de um guia). */
export function isOfficialLegend(legend: ProductLegend): boolean {
  return legendImages(legend).length > 0;
}

/** URL pública de uma imagem de legenda (respeita a base do Vite). */
export function legendImageUrl(file: string): string {
  return `${import.meta.env.BASE_URL}legends/${file}`;
}

// ---------------------------------------------------------------------------
// Dia ou noite (grupos das composições que mudam de receita)
// ---------------------------------------------------------------------------

/** Centro aproximado da Amazônia Legal, usado para decidir "dia" ou "noite". */
export const AMAZON_CENTER = { lon: -60, lat: -5 } as const;

/**
 * Altura do Sol (graus) num instante e lugar. Aproximação simples (declinação
 * pela fórmula de Cooper, sem a equação do tempo: erro de ~15 min), suficiente
 * para escolher a chave de dia ou de noite.
 */
export function solarElevationDeg(time: number, lat: number, lon: number): number {
  const d = new Date(time);
  const startOfYear = Date.UTC(d.getUTCFullYear(), 0, 1);
  const dayOfYear = Math.floor((time - startOfYear) / 86_400_000) + 1;
  const rad = Math.PI / 180;
  const declination = 23.44 * Math.sin(((2 * Math.PI) / 365) * (284 + dayOfYear));
  const utcHours = d.getUTCHours() + d.getUTCMinutes() / 60 + d.getUTCSeconds() / 3600;
  const hourAngle = 15 * (utcHours + lon / 15 - 12);
  const sinEl =
    Math.sin(lat * rad) * Math.sin(declination * rad) +
    Math.cos(lat * rad) * Math.cos(declination * rad) * Math.cos(hourAngle * rad);
  return Math.asin(Math.max(-1, Math.min(1, sinEl))) / rad;
}

/** "Dia" quando o Sol está a mais de 5° acima do horizonte no centro da Amazônia. */
export function daypartAt(time: number, where: { lat: number; lon: number } = AMAZON_CENTER): Daypart {
  return solarElevationDeg(time, where.lat, where.lon) > 5 ? 'day' : 'night';
}

/** Grupo a mostrar: o do período pedido, ou o primeiro. */
export function pickGroup(legend: KeyLegend, daypart: Daypart | null): KeyGroup {
  if (daypart) {
    const match = legend.groups.find((g) => g.daypart === daypart);
    if (match) return match;
  }
  return legend.groups[0];
}

/**
 * Linhas (de 2 itens) do maior grupo de uma chave com dia/noite: a legenda
 * reserva essa altura para não mudar de tamanho quando o loop cruza o nascer
 * ou o pôr do sol.
 */
export function keyRows(legend: KeyLegend): number {
  return Math.max(...legend.groups.map((g) => Math.ceil(g.items.length / 2)));
}

/** A legenda tem grupos diferentes para dia e noite. */
export function hasDayparts(legend: ProductLegend): legend is KeyLegend {
  return legend.kind === 'key' && legend.groups.some((g) => g.daypart);
}

// ---------------------------------------------------------------------------
// Camadas que aparecem na legenda
// ---------------------------------------------------------------------------

export interface LegendLayerInput {
  uid: string;
  productId: string;
  opacity: number;
  visible: boolean;
  /** Sem imagem no quadro atual (o STAR não publicou esta varredura): não está na tela. */
  ausente?: boolean;
}

export interface LegendLayer {
  uid: string;
  productId: string;
  opacity: number;
  /** Uma camada opaca acima esconde esta por completo. */
  covered: boolean;
  /** Sem imagem no quadro atual: não cobre as de baixo, e a legenda diz por quê. */
  ausente?: boolean;
}

/**
 * Camadas GOES visíveis, do TOPO para a BASE (a pilha vem da base para o topo).
 * Camadas ocultas ou com opacidade zero ficam de fora; as que estão abaixo de
 * uma camada 100% opaca são marcadas como cobertas — menos por uma camada
 * ausente no quadro, que não está na tela.
 */
export function legendLayers(stack: readonly LegendLayerInput[]): LegendLayer[] {
  const out: LegendLayer[] = [];
  let covered = false;
  for (let i = stack.length - 1; i >= 0; i--) {
    const l = stack[i];
    if (!l.visible || l.opacity <= 0.01) continue;
    if (l.ausente) {
      out.push({ uid: l.uid, productId: l.productId, opacity: l.opacity, covered, ausente: true });
      continue;
    }
    out.push({ uid: l.uid, productId: l.productId, opacity: l.opacity, covered });
    if (l.opacity >= 0.99) covered = true;
  }
  return out;
}
